import { z } from 'zod';

import { documentInputRaw, maskDocumentInput } from '@/shared/format/document';

import {
  messageGroupWireLabel,
  operationStatusWireLabel,
  operationTypeWireLabel,
  pixTypeWireLabel,
} from './spi.constants';

// Contrato de fio da Gestão SPI (U27), tirado do TIPO TS do legado `UYZY@d999fc0be`
// (`services/spiManagement/spiManagement.types.ts`), não do que a tela lê. Sem envelope: o
// `bankingAxiosApi.get<T>` do legado devolve o corpo na raiz (`spiManagement.ts:19-22,33-36,84-87`).
// Todo valor monetário é `Cents` (o `formatCurrencyInCents` do legado divide por 100,
// `helpers/formats/Currency.ts:32-46`; o `CurrencyFormField` multiplica por 100, `:100-108`).

/** `ISPIManagementCheckingAccountBalance` (`spiManagement.types.ts:1-6`). Valores em centavos. */
export const checkingAccountBalanceSchema = z.looseObject({
  errorNumber: z.number(),
  blockedBalance: z.number(),
  availableBalance: z.number(),
  totalBalance: z.number(),
});
export type CheckingAccountBalance = z.infer<typeof checkingAccountBalanceSchema>;

/** `ISPIManagementMovementSummary` (`spiManagement.types.ts:8-14`): só `quantity` é opcional lá. */
export const movementSummarySchema = z.looseObject({
  date: z.string(),
  amount: z.number(),
  situation: z.string(),
  type: z.string(),
  quantity: z.number().optional(),
});
export type MovementSummary = z.infer<typeof movementSummarySchema>;

/** `ISPIManagementPiAccountBalance` (`spiManagement.types.ts:16-29`). */
export const piAccountBalanceSchema = z.looseObject({
  baseBalance: z.number(),
  currentBalance: z.number(),
  totalPiMovementCredit: movementSummarySchema,
  totalPiMovementDebit: movementSummarySchema,
  totalPaymentCreditEffectived: movementSummarySchema,
  totalPaymentDebitEffectived: movementSummarySchema,
  totalPaymentCreditInTransit: movementSummarySchema,
  totalPaymentDebitInTransit: movementSummarySchema,
  totalInternalPayment: movementSummarySchema,
  totalInternalPaymentCredit: movementSummarySchema,
  totalInternalPaymentDebit: movementSummarySchema,
  lastMovementDateTime: z.string(),
});
export type PiAccountBalance = z.infer<typeof piAccountBalanceSchema>;

/** `IPiAccountExternalOperation` (`spiManagement.types.ts:46-77`): todo campo é opcional lá. */
export const piOperationSchema = z.looseObject({
  id: z.string().optional(),
  sentAt: z.string().optional(),
  sentMessageCode: z.string().optional(),
  sourceIspbCode: z.string().optional(),
  destinationIspbCode: z.string().optional(),
  /** Centavos (`formatCurrencyInCents`, `SPIConsultFormList.tsx:92`). */
  amount: z.number().optional(),
  status: z.string().optional(),
  error: z.string().optional(),
  returnMessageCode: z.string().optional(),
  returnedAt: z.string().optional(),
  operationId: z.string().optional(),
  payerBankName: z.string().optional(),
  payerBankCOMPECode: z.string().optional(),
  payerName: z.string().optional(),
  payerRegistrationNumber: z.string().optional(),
  payerAgency: z.string().optional(),
  payerAccountNumber: z.string().optional(),
  payerAccountType: z.string().optional(),
  receiverBankName: z.string().optional(),
  receiverBankCOMPECode: z.string().optional(),
  receiverName: z.string().optional(),
  receiverRegistrationNumber: z.string().optional(),
  receiverAgency: z.string().optional(),
  receiverAccountNumber: z.string().optional(),
  receiverAccountType: z.string().optional(),
  nature: z.string().optional(),
  description: z.string().optional(),
  entryChannel: z.string().optional(),
  sentMessageXml: z.string().optional(),
  returnedMessageXml: z.string().optional(),
});
export type PiOperationWire = z.infer<typeof piOperationSchema>;

/** `IPiAccountExternalOperationsResponse` (`spiManagement.types.ts:79-85`). Sem página: vem tudo. */
export const piOperationsResponseSchema = z.looseObject({
  items: z.array(piOperationSchema),
  totalCreditMessages: z.number().optional(),
  totalDebitMessages: z.number().optional(),
  totalCredit: z.number().optional(),
  totalDebit: z.number().optional(),
});
export type PiOperationsResponse = z.infer<typeof piOperationsResponseSchema>;

/** A linha como a tela a usa: o `id` do legado (`SPIManagementContext.tsx:198`). */
export type PiOperation = PiOperationWire & { readonly id: string };

/**
 * `{ id: item.operationId, ...item }` (`SPIManagementContext.tsx:198`): o spread vem **depois**, então
 * um `id` presente no corpo vence o `operationId`; sem a chave `id`, fica o `operationId`. Sem nenhum
 * dos dois, o índice da linha (o DataGrid do legado exigia `id`, e a linha sem ele quebrava a tabela).
 */
export function toPiOperation(item: PiOperationWire, index: number): PiOperation {
  const id = 'id' in item ? item.id : item.operationId;
  return { ...item, id: id ?? String(index) };
}

/** Corpo do `POST /FrontWebSPI/{Contribution,Withdraw}` — `ITransactionAction` (`spiManagement.types.ts:31-35`). */
export const transactionActionBodySchema = z.object({
  /** Centavos: o `CurrencyFormField` grava `valor * 100` arredondado (`CurrencyFormField.tsx:100-108`). */
  amount: z.number().int(),
  code: z.string(),
  sessionId: z.string(),
});
export type TransactionActionBody = z.infer<typeof transactionActionBodySchema>;

/**
 * Corpo na ordem do legado (`SPIManagementRequestContainer.tsx:76-80`): `{ code, sessionId, ...formData }`.
 * O formulário só tem `amount`, então sai `{ code, sessionId, amount }`.
 */
export function transactionActionBody(amount: number, proof: { readonly sessionId: string; readonly code: string }): TransactionActionBody {
  return { code: proof.code, sessionId: proof.sessionId, amount };
}

// ---------------------------------------------------------------------------------------------
// Filtros da consulta de operações PIX
// ---------------------------------------------------------------------------------------------

/**
 * Filtros aplicados, legíveis (URL e rascunho). Os nomes são os do formulário do legado
 * (`SPIConsultSchema.ts:53-67`); `amountFrom`/`amountTo` em centavos, como string de dígitos.
 */
export interface SpiOperationFilters {
  readonly messageGroup?: string | undefined;
  readonly operationType?: string | undefined;
  /** `datetime-local`: `YYYY-MM-DDTHH:mm`, hora de parede, sem fuso (`SPIConsultForm.tsx:76-101`). */
  readonly startDate?: string | undefined;
  readonly endDate?: string | undefined;
  readonly amountFrom?: string | undefined;
  readonly amountTo?: string | undefined;
  /** ISPB do banco escolhido (`SPIConsultForm.tsx:124-132`). */
  readonly counterpartyBankIspb?: string | undefined;
  /** O `value` da opção do autocomplete (código COMPE): o `name` do campo é este (`:120`). */
  readonly counterpartyBankIspbDisplay?: string | undefined;
  /** O `displayName` do autocomplete (`:122`). */
  readonly bankName?: string | undefined;
  readonly operationStatus?: string | undefined;
  /**
   * CPF/CNPJ **cru** (só letras e dígitos), como o `DocumentFormField` guarda. No legado o campo
   * guardava o valor **com** a máscara do `MaskedInput` (`SPIConsultForm.tsx:146-156`), e é assim que
   * ele vai no fio: `spiOperationsQuery` remascara antes de montar a query.
   */
  readonly registrationNumberSearch?: string | undefined;
  readonly endToEndId?: string | undefined;
  readonly errorMessageFilter?: string | undefined;
  readonly entryChannel?: string | undefined;
  readonly pixType?: string | undefined;
  /** Seletores mexidos, na ordem do primeiro toque, por vírgula (ver `spiOperationsQuery`). */
  readonly auxiliares?: string | undefined;
}

/** Os cinco seletores que gravam campos auxiliares no fio (`SelectFormField`, `AutocompleteField`). */
export const SPI_AUX_GROUPS = ['messageGroup', 'operationType', 'bank', 'operationStatus', 'pixType'] as const;
export type SpiAuxGroup = (typeof SPI_AUX_GROUPS)[number];

/** As chaves do `defaultValuesSPIConsultSchema`, na ordem em que o RHF do legado as cria (`:53-67`). */
export const SPI_FILTER_KEYS = [
  'messageGroup',
  'operationType',
  'startDate',
  'endDate',
  'amountFrom',
  'amountTo',
  'counterpartyBankIspb',
  'operationStatus',
  'registrationNumberSearch',
  'endToEndId',
  'errorMessageFilter',
  'entryChannel',
  'pixType',
] as const satisfies readonly (keyof SpiOperationFilters)[];

/** O que a URL guarda como consulta aplicada. */
export const SPI_APPLIED_KEYS = [
  ...SPI_FILTER_KEYS,
  'counterpartyBankIspbDisplay',
  'bankName',
  'auxiliares',
] as const satisfies readonly (keyof SpiOperationFilters)[];

export interface SpiOperationsParams {
  readonly filters: SpiOperationFilters;
  /** `FEATURE_FLAGS.CNPJ_ALPHANUMERIC`: o `cleanDocumentValue` do legado mantém letras. */
  readonly alphanumeric: boolean;
}

const present = (value: string | undefined): value is string => value !== undefined && value !== '';
const orNull = (value: string | undefined) => (present(value) ? value : null);
const numberOrNull = (value: string | undefined) => (present(value) ? Number(value) : null);

/**
 * `cleanDocumentValue` (`helpers/validations/formFieldValidator.ts:139-142`): tira só os separadores;
 * com `CNPJ_ALPHANUMERIC` (ligada em STG) o CNPJ tem letras e elas ficam. Nunca `replace(/\D/g,'')`.
 */
export function cleanDocument(value: string, alphanumeric: boolean): string {
  return alphanumeric ? value.replace(/[^A-Za-z0-9]/g, '') : value.replace(/\D/g, '');
}

/** `auxiliares` lido da URL: só grupos conhecidos, sem repetir, na ordem dada. */
export function parseAuxiliaries(value: string | undefined): SpiAuxGroup[] | undefined {
  if (value === undefined) return undefined;
  const out: SpiAuxGroup[] = [];
  for (const part of value.split(',')) {
    const group = SPI_AUX_GROUPS.find((candidate) => candidate === part);
    if (group && !out.includes(group)) out.push(group);
  }
  return out;
}

const groupHasValue = (filters: SpiOperationFilters, group: SpiAuxGroup) =>
  present(group === 'bank' ? filters.counterpartyBankIspbDisplay : filters[group]);

/**
 * Ordem dos seletores mexidos: a de `auxiliares` e, depois, qualquer seletor com valor que não esteja
 * lá (no legado, ter valor implica ter sido tocado), na ordem da tela.
 */
export function auxiliaryOrder(filters: SpiOperationFilters): SpiAuxGroup[] {
  const order = parseAuxiliaries(filters.auxiliares) ?? [];
  for (const group of SPI_AUX_GROUPS) if (!order.includes(group) && groupHasValue(filters, group)) order.push(group);
  return order;
}

const AUX_DISPLAY = {
  messageGroup: ['messageGroupDisplay', messageGroupWireLabel],
  operationType: ['operationTypeDisplay', operationTypeWireLabel],
  operationStatus: ['operationStatusDisplay', operationStatusWireLabel],
  pixType: ['pixTypeDisplay', pixTypeWireLabel],
} as const satisfies Record<Exclude<SpiAuxGroup, 'bank'>, readonly [string, (value: string | undefined) => string | undefined]>;

/**
 * Query de `GET /FrontWebSPI/PiAccountExternalOperations` (`spiManagement.ts:68-88`), na ordem do
 * legado. O serializador é `Qs.stringify(params, { arrayFormat: 'repeat' })` (`axiosApi.ts:50`):
 * `null` vira `chave=` e `undefined` some.
 *
 * 1. as 13 chaves do `defaultValuesSPIConsultSchema`, sempre, na ordem de lá (vazio vai `null`);
 * 2. as chaves que cada seletor cria no primeiro toque, na ordem do toque:
 *    - select escolhido: `<campo>Display` com o rótulo; limpo pelo "x": `<campo>Display=null`
 *      (`SelectFormField.tsx:113-124`);
 *    - banco escolhido: `counterpartyBankIspbDisplay` (o código) e `bankName` (o rótulo), e o
 *      `onChange` da tela grava o ISPB em `counterpartyBankIspb` (`SPIConsultForm.tsx:124-132`);
 *    - banco limpo: `counterpartyBankIspbDisplay` vira `undefined` e **some**, `bankName=` e
 *      `counterpartyBankIspb=` (`AutocompleteField.tsx:33-35` + o `onChange` da tela);
 *    - o `counterpartyBankIspbDisplayDiscriminator` é `undefined` nos dois casos (a opção de banco não
 *      tem discriminador) e o `qs` descarta;
 * 3. por último `registrationNumber`, só quando há documento (senão `undefined`, e o `qs` descarta).
 *
 * `page` e `rowsPerPage` **não** entram: o legado nunca os manda (`SPIConsultContainer.tsx:36-41`,
 * `paginationMode='client'` em `SPIConsultFormList.tsx:193`). Divergência D2, registrada, não corrigida.
 */
export function spiOperationsQuery(params: SpiOperationsParams): Record<string, unknown> {
  const { filters } = params;
  const touched = auxiliaryOrder(filters);
  const bankCleared = touched.includes('bank') && !present(filters.counterpartyBankIspbDisplay);
  const query: Record<string, unknown> = {
    messageGroup: numberOrNull(filters.messageGroup),
    operationType: numberOrNull(filters.operationType),
    startDate: orNull(filters.startDate),
    endDate: orNull(filters.endDate),
    amountFrom: numberOrNull(filters.amountFrom),
    amountTo: numberOrNull(filters.amountTo),
    counterpartyBankIspb: bankCleared ? '' : numberOrNull(filters.counterpartyBankIspb),
    operationStatus: numberOrNull(filters.operationStatus),
    // O campo do legado guarda o documento **com** máscara; o `DocumentFormField` guarda cru, então o
    // fio remascara para mandar exatamente o que o legado mandava (`SPIConsultForm.tsx:146-156`).
    registrationNumberSearch: present(filters.registrationNumberSearch)
      ? maskDocumentInput(documentInputRaw(filters.registrationNumberSearch, { alphanumeric: params.alphanumeric }))
      : null,
    endToEndId: orNull(filters.endToEndId),
    errorMessageFilter: orNull(filters.errorMessageFilter),
    entryChannel: orNull(filters.entryChannel),
    pixType: numberOrNull(filters.pixType),
  };
  for (const group of touched) {
    if (group === 'bank') {
      if (!bankCleared) query['counterpartyBankIspbDisplay'] = filters.counterpartyBankIspbDisplay ?? null;
      query['bankName'] = bankCleared ? '' : (filters.bankName ?? '');
    } else {
      const [key, label] = AUX_DISPLAY[group];
      query[key] = present(filters[group]) ? (label(filters[group]) ?? null) : null;
    }
  }
  // `{ ...filters, registrationNumber }`: chave nova, então entra por último (`spiManagement.ts:72-77`).
  query['registrationNumber'] = present(filters.registrationNumberSearch)
    ? cleanDocument(filters.registrationNumberSearch, params.alphanumeric)
    : undefined;
  return query;
}

/**
 * Query do `GET /FrontWebSPI/PiAccountExternalOperationsXlsx` (`spiManagement.ts:90-103`): os mesmos
 * filtros **sem** o `registrationNumber` limpo — o XLS manda só o `registrationNumberSearch`, com a
 * máscara. Divergência D1 do legado: registrada, não corrigida.
 */
export function spiOperationsXlsxQuery(params: SpiOperationsParams): Record<string, unknown> {
  const query = spiOperationsQuery(params);
  delete query['registrationNumber'];
  return query;
}
